import { Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ORDER_BUILDER_ROUTES } from '@ordersnproducts/modules/shared/constants/order-builder-routes';
import { productLineCodeToAlphaMap } from '@ordersnproducts/modules/shared/constants/product-lines-codes';
import { shippingConditionCodeToAlphaMap } from '@ordersnproducts/modules/shared/constants/shipping-conditions-codes';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { UNITS as units } from '@ordersnproducts/modules/shared/constants/units';
import * as moment from 'moment';
import {
  DimensionNilValue,
  GoogleAnalyticsEventAction,
  GoogleAnalyticsEventLabel,
  GoogleAnalyticsEventType,
  GoogleAnalyticsEventCategory,
  GoogleAnalyticsEvent,
  GoogleAnalyticsEventStatus,
} from '../modules/google-analytics-event-type';
import { ANALYTICS_DATE_FORMAT } from 'app/modules/google-analytics-type';
import { getPaymentTermDescFromCode } from '@ordersnproducts/utils/payments-terms';

export class AnalyticsTrackingBase {
  // State

  public deliveryMethod: string;
  public productLineDesc: string;
  public pointOfDeliveryId: string;
  public orderId: string;
  public jobsiteId: string;
  public productDescription: string[];
  public productQuantity: string[];
  public productUnit: number[];
  public slump: string[];
  public loadSize: string[];
  public spacing: string[];
  public deliveryDate: string[];
  public deliveryTime: string[];
  public paymentTerm: string[];
  public productId: string[];
  public quantity: string[];
  public activeStep: number;
  public cleanUp: boolean;
  private checkoutStep: number;

  constructor() {}

  // Selectors
  selectProductLine(state, _, translate) {
    const productLineDesc = state.businessLinePage.businessLine.selected.productTypeCode
      .split(',')
      .reduce((productLineDescAcc, productLineCode, index, productLineCodes) => {
        const productLineAlphaCode = productLineCodeToAlphaMap.get(productLineCode);
        let productLineDescTrans = translate(`globalFilters.productLines.${productLineAlphaCode}`);
        if (index < productLineCodes.length - 1) {
          productLineDescTrans += ' / ';
        }
        return productLineDescAcc + productLineDescTrans;
      }, '');

    return productLineDesc || state.businessLinePage.businessLine.selected.productLineDesc;
  }

  selectDeliveryMethod(state, _, translate) {
    const shippingConditionNameCode = shippingConditionCodeToAlphaMap.get(
      state.businessLinePage.shippingCondition.selected.shippingConditionCode,
    );

    return translate(`views.product.selection.shipping_condition.${shippingConditionNameCode}`);
  }

  selectPointOfDeliveryId(state) {
    return state.locationPage.pointOfDelivery.selected.shipmentLocationCode;
  }

  selectOrderId(state) {
    return state.reviewPage.draft.orderDraft.orderId;
  }

  selectJobsiteId(state) {
    return state.locationPage.jobsite.selected.shipmentLocationCode;
  }

  selectProductDescription(state) {
    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        const productId = state.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.productSelect;
        return state.rmsSpecificationPage.productsLegacyState.availableProducts
          .filter(product => product.comm_product.comm_product_id === productId)
          .map(product => {
            return product.comm_product.comm_long_desc;
          });
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailSpecification):
        return state.retailSpecificationPage.retailSpecificationState.forms.map(form => {
          return `${form.values.productSelect.commercialCode.replace(/^0+/, '')} / ${
            form.values.productSelect.commercialDesc
          }`;
        });
      default:
        return state.specificationPage.specificationForm.forms.map(form => {
          return `${form.values.productSelect.commercialCode.replace(/^0+/, '')} / ${
            form.values.productSelect.commercialDesc
          }`;
        });
    }
  }

  isInContextOf(state, step: ORDER_BUILDER_ROUTES) {
    const stepIndex = state.stepper.steps.findIndex(({ route }) => route === step);
    return stepIndex !== -1;
  }

  selectProductUnit(state) {
    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        return [units.CUBIC_METERS];
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailSpecification):
        return state.retailSpecificationPage.retailSpecificationState.forms.map(form => form.values.unit);
      default:
        return state.specificationPage.specificationForm.forms.map(form => form.values.unit);
    }
  }

  selectProductQuantity(state) {
    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        return state.rmsSpecificationPage.rmsSpecificationFormState.forms.map(form => form.values.quantity);
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailSpecification):
        return state.retailSpecificationPage.retailSpecificationState.map(form => form.values.quantity);
      default:
        return state.specificationPage.specificationForm.forms.map(form => form.values.quantity);
    }
  }

  selectPaymentTerm(state) {
    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        const paymentTermDesc = getPaymentTermDescFromCode(
          state.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.paymentTerms,
        );
        return paymentTermDesc;
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailSpecification):
        return state.retailSpecificationPage.retailSpecificationState.forms.map(form => {
          const productId = form.values.productSelect.product.productId;
          const paymentTermId = form.values.paymentTerms;
          const selectedPaymentTerm = state.specificationPage.paymentTerm.availablePaymentTerms[productId].find(
            paymentTerm => paymentTerm.paymentTermId === paymentTermId,
          );
          return selectedPaymentTerm.paymentTermType.paymentTermTypeCode;
        });
      default:
        return state.specificationPage.paymentTerm.defaultPaymentTerm.paymentTermType.paymentTermTypeCode;
    }
  }

  selectProductId(state) {
    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        return state.rmsSpecificationPage.rmsSpecificationFormState.forms.map(form => form.values.productSelect);
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailSpecification):
        return state.retailSpecificationPage.retailSpecificationState.forms.map(
          form => form.values.productSelect.product.productId,
        );
      default:
        return state.specificationPage.specificationForm.forms.map(form => form.values.productSelect.product.productId);
    }
  }

  selectDeliveryDate(state, injector: Injector) {
    const translate = injector.get<TranslateService>(TranslateService);
    const format = translate.instant(ANALYTICS_DATE_FORMAT);

    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        return moment(state.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.scheduleMoment._d).format(
          format,
        );
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailReview):
        return moment(state.retailReviewPage.offerState.openBuckets[0].selectedDate).format(format);
      default:
        return state.specificationPage.specificationForm.forms.map(form => {
          return moment(form.values.date).format(format);
        });
    }
  }

  selectDeliveryTime(state, injector: Injector) {
    const translate = injector.get<TranslateService>(TranslateService);
    const format = translate.instant('global.formatTime');

    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        return moment(state.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.scheduleMoment._d).format(
          format,
        );
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RetailReview):
        return moment(state.retailReviewPage.offerState.openBuckets[0].selectedDate).format(format);
      default:
        return state.specificationPage.specificationForm.forms.map(form => {
          return form.values.time.format(format);
        });
    }
  }

  selectActiveStep(state) {
    return state.stepper.activeStep;
  }

  selectCleanUp(state) {
    switch (true) {
      case this.isInContextOf(state, ORDER_BUILDER_ROUTES.RmsSpecification):
        return state.rmsSpecificationPage.rmsSpecificationFormState.forms[0].values.kicker;
      default:
        if (!state.specificationPage.specificationForm.forms.length) {
          return undefined;
        }
        const specValues = state.specificationPage.specificationForm.forms[0].values;
        return specValues.kicker;
    }
  }

  createSpecificationPageReport(result): any {
    if (!result.productDescription) {
      return null;
    }

    const products = this.getProductsSpecification(result);

    return {
      ...this.businessLineData(
        GoogleAnalyticsEventType.AddToCart,
        GoogleAnalyticsEventAction.AddToCart,
        GoogleAnalyticsEventLabel.AddToCart,
      ),
      ecommerce: {
        add: {
          products: products,
        },
      },
    };
  }

  /* TODO: All transaction related functions not in use.
  / Removed at https://cemexpmo.atlassian.net/browse/CX018GO1905-1153.
  / Event will be reimplemented later */
  transactionRequest(): GoogleAnalyticsEvent {
    return this.businessLineTranslation(
      GoogleAnalyticsEventType.Transaction,
      GoogleAnalyticsEventAction.Transaction,
      GoogleAnalyticsEventLabel.S2Orders,
    );
  }

  createBusinessLinePageLoadedReport(): GoogleAnalyticsEvent {
    return this.businessLineData(
      GoogleAnalyticsEventType.Checkout,
      GoogleAnalyticsEventAction.OrderRequest,
      GoogleAnalyticsEventLabel.OrderStep_ProductLine,
    );
  }

  createLocationTransaction(result): any {
    return {
      ...this.businessLineTranslation(
        GoogleAnalyticsEventType.Transaction,
        GoogleAnalyticsEventAction.Transaction,
        GoogleAnalyticsEventLabel.S2Orders,
      ),
      ...this.productLineData(result),
    };
  }

  createOnHoldReport(orderId: number, holdStatus: GoogleAnalyticsEventStatus): GoogleAnalyticsEvent {
    const action = {
      intent: GoogleAnalyticsEventAction.OnHoldOrder,
      error: GoogleAnalyticsEventAction.Rejected,
      success: GoogleAnalyticsEventAction.Successful,
    };

    return {
      event: GoogleAnalyticsEventType.Custom,
      eventCategory: GoogleAnalyticsEventCategory.HoldOrder,
      eventAction: action[holdStatus],
      eventLabel: orderId.toString(),
    };
  }

  createResumeReport(orderId: number, resumeStatus: GoogleAnalyticsEventStatus): any {
    const action = {
      intent: GoogleAnalyticsEventAction.ResumeOrder,
      error: GoogleAnalyticsEventAction.Rejected,
      success: GoogleAnalyticsEventAction.Successful,
    };

    return {
      event: GoogleAnalyticsEventType.Custom,
      eventCategory: GoogleAnalyticsEventCategory.ResumeOrder,
      eventAction: action[resumeStatus],
      eventLabel: orderId.toString(),
    };
  }

  createCancelOrderReport(orderId: number, cancelStatus: GoogleAnalyticsEventStatus): GoogleAnalyticsEvent {
    const action = {
      intent: GoogleAnalyticsEventAction.CancelOrder,
      error: GoogleAnalyticsEventAction.Rejected,
      success: GoogleAnalyticsEventAction.Successful,
    };

    return {
      event: GoogleAnalyticsEventType.Custom,
      eventCategory: GoogleAnalyticsEventCategory.CancelOrder,
      eventAction: action[cancelStatus],
      eventLabel: orderId.toString(),
    };
  }

  createLocationPageLoadedReport(result): any {
    return {
      ...this.businessLineData(
        GoogleAnalyticsEventType.Checkout,
        GoogleAnalyticsEventAction.OrderRequest,
        GoogleAnalyticsEventLabel.OrderStep_Location,
      ),
      ...this.productLineData(result),
    };
  }

  createSpecificationPageLoadedReport(result): any {
    return {
      ...this.businessLineData(
        GoogleAnalyticsEventType.Checkout,
        GoogleAnalyticsEventAction.OrderRequest,
        GoogleAnalyticsEventLabel.OrderStep_Specifications,
      ),
      ...this.productLineData(result),
      ...this.locationData(result),
    };
  }

  // transaction analytics
  createSpecificationTransaction(result): any {
    return {
      ...this.businessLineTranslation(
        GoogleAnalyticsEventType.Transaction,
        GoogleAnalyticsEventAction.Transaction,
        GoogleAnalyticsEventLabel.S2Orders,
      ),
      ...this.productLineData(result),
      ...this.locationData(result),
    };
  }
  // transaction analytics

  createReviewPageLoadedReport(result): any {
    this.checkoutStep = 1;
    return this.reviewAndSummaryLoadedReport(result, GoogleAnalyticsEventLabel.OrderStep_Review);
  }

  createReviewPageTransaction(result): any {
    return this.reviewAndSummaryTransaction(result, GoogleAnalyticsEventLabel.OrderStep_Review);
  }

  createSummaryPageLoaded(result) {
    return this.reviewAndSummaryLoadedReport(result, GoogleAnalyticsEventLabel.OrderStep_Summary);
  }

  createSummaryPageTransaction(result) {
    return this.reviewAndSummaryTransaction(result, GoogleAnalyticsEventLabel.OrderStep_Summary);
  }

  createSummaryPageReport(result, orderId?: string) {
    this.checkoutStep++;
    return this.reviewAndSummaryLoadedReport(result, GoogleAnalyticsEventLabel.OrderStep_FinalStep, orderId);
  }

  createOrderStatusReport(result, success, orderId?: string) {
    this.checkoutStep++;
    const label = success ? GoogleAnalyticsEventLabel.OrderStep_Success : GoogleAnalyticsEventLabel.OrderStep_Error;
    return this.reviewAndSummaryLoadedReport(result, label, orderId);
  }

  changeCheckoutStep(add) {
    this.checkoutStep += add ? 1 : -1;
  }

  createReportNoSlotAvailable(): GoogleAnalyticsEvent {
    return this.businessLineData(
      GoogleAnalyticsEventType.Custom,
      GoogleAnalyticsEventAction.Error,
      GoogleAnalyticsEventLabel.NoAvailability,
    );
  }

  private businessLineData(event, action, label): GoogleAnalyticsEvent {
    return {
      event: event,
      eventCategory: GoogleAnalyticsEventCategory.NewOrder,
      eventAction: action,
      eventLabel: label,
    };
  }

  private businessLineTranslation(event, action, label): GoogleAnalyticsEvent {
    return {
      event: event,
      eventCategory: GoogleAnalyticsEventCategory.Ecommerce,
      eventAction: action,
      eventLabel: label,
    };
  }

  private productLineData(result) {
    return {
      productLine: result.productLineDesc,
      deliveryMethod: result.deliveryMethod,
    };
  }

  private locationData(result) {
    return {
      jobsiteId: result.jobsiteId,
      pointOfDelivery: result.pointOfDeliveryId ? result.pointOfDeliveryId : result.jobsiteId,
      experienceCode: _safe(() => result.store.locationPage.experiences.activeExperienceCode)
        ? result.store.locationPage.experiences.activeExperienceCode
        : 'Other',
    };
  }

  private getProductsSpecification(result) {
    const products = [];
    const orArray = (element, i) => (Array.isArray(element) ? element[i] : element);
    result.productDescription.forEach((_item, index) => {
      const readyMixSpecificationFormState = this.isInContextOf(result.store, ORDER_BUILDER_ROUTES.RmsSpecification)
        ? result.store.rmsSpecificationPage.rmsSpecificationFormState
        : result.store.specificationPage.specificationForm;
      const readyMixValues = _safe(() => readyMixSpecificationFormState.forms[index].values, {});
      products.push({
        name: result.productDescription !== null ? result.productDescription[index] : '',
        id: result.productId !== null ? result.productId[index] : '',
        category: result.productLineDesc,
        quantity: result.productQuantity !== null ? result.productQuantity[index] : '',
        dimension19: orArray(result.deliveryDate, index) || DimensionNilValue,
        dimension20: _safe(() => result.deliveryTime[index], DimensionNilValue),
        dimension21: _safe(() => result.productUnit[index], DimensionNilValue),
        dimension22: readyMixValues.element || DimensionNilValue,
        dimension23: readyMixValues.timePerLoad || DimensionNilValue,
        dimension24: readyMixValues.unloadType || DimensionNilValue,
        dimension25: result.cleanUp !== undefined ? (result.cleanUp ? 'Yes' : 'No') : DimensionNilValue,
        dimension26: orArray(result.paymentTerm, index) || DimensionNilValue,
        dimension27: readyMixValues.dischargeTime || DimensionNilValue,
      });
    });

    return products;
  }

  private reviewAndSummaryLoadedReport(result, eventLabel, orderId?: string) {
    if (!result.productDescription) {
      return null;
    }

    const products = this.getProductsSpecification(result);

    const report = {
      ...this.businessLineData(GoogleAnalyticsEventType.Checkout, GoogleAnalyticsEventAction.OrderRequest, eventLabel),
      ...this.productLineData(result),
      ...this.locationData(result),
      ecommerce: {
        checkout: {
          actionField: {
            step: this.checkoutStep,
          },
          products: products,
        },
      },
    };

    // Include orderId if any given
    if (orderId !== undefined) {
      return { ...report, orderId };
    } else {
      return report;
    }
  }

  private reviewAndSummaryTransaction(result, eventLabel) {
    if (!result.productDescription) {
      return null;
    }

    const products = this.getProductsSpecification(result);

    return {
      ...this.businessLineTranslation(
        GoogleAnalyticsEventType.Transaction,
        GoogleAnalyticsEventAction.OrderRequest,
        eventLabel,
      ),
      ...this.productLineData(result),
      ...this.locationData(result),
      ecommerce: {
        purchase: {
          actionField: {
            step: this.checkoutStep,
          },
          products: products,
        },
      },
    };
  }
}
